import { buildClientImgixUrl, resolveByosImgixSrc } from '@air/component-imgix-image';

/** This type represents the extension type that our users can export FROM */
export type ImgixSupportedExportExt =
  | 'ai'
  | 'apng'
  | 'avif'
  | 'bmp'
  | 'eps'
  | 'gif'
  | 'heic'
  | 'ico'
  | 'icns'
  | 'jpg'
  | 'jpeg'
  | 'pct'
  // | 'pdf'
  | 'pjpeg'
  | 'png'
  | 'psd'
  | 'svg'
  | 'tif'
  | 'webp';
// | 'avi'
// | 'h264'
// | 'hls'
// | 'mov'
// | 'mp4'
// | 'wmv'
// | 'webm';

/**
 * This function returns a boolean indicating if the extension you are trying to export can be exported.
 * THIS IS NOT the same as the FORMAT of the export. Meaning, a JPG can be exported, but the EXPORT FORMAT
 * (like it could be exported as a PNG) is different. @see extIsSupportedExportFormat
 * @param ext The extension of the file you are trying to export
 * @returns boolean
 */
export const extCanBeExported = (ext: string): ext is ImgixSupportedExportExt =>
  /**
   * @description List of supported Imgix import formats
   * @see https://docs.imgix.com/best-practices/guide-to-image-types#which-image-formats-can-be-served-with-imgix
   */
  [
    'ai',
    'apng',
    'avif',
    'bmp',
    'eps',
    'gif',
    'heic',
    'ico',
    'icns',
    'jpg',
    'jpeg',
    'pct',
    // 'pdf',
    'pjpeg',
    'png',
    'psd',
    'svg',
    'tif',
    'webp',
    // 'avi',
    // 'h264',
    // 'hls',
    // 'mov',
    // 'mp4',
    // 'wmv',
    // 'webm',
  ].includes(ext.toLowerCase());

/**
 * Returns true if the extension can be displayed natively in an <img> element
 * across ALL browsers (including older versions).
 * Formats with universal support: JPEG, PNG, GIF
 * Note: WebP is NOT included as it's not supported in Safari <14 (2020) and older browsers
 */
export const extCanBeDisplayedNatively = (ext: string): boolean =>
  ['jpg', 'jpeg', 'png', 'gif'].includes(ext.toLowerCase());

type ImgixExportSourceAssets = {
  image?: string | null;
  original?: string | null;
};

/**
 * Select the most reliable image source for imgix exports.
 *
 * SVG, PSD, and EPS files should prefer the transcoded preview URL (`assets.image`)
 * because the original source can fail to rasterize as expected in export flows.
 */
const toExportableImgixUrl = (url?: string | null): string | undefined => {
  if (!url) {
    return undefined;
  }

  return resolveByosImgixSrc(url);
};

export const getBestImgixExportSource = (ext: string, assets: ImgixExportSourceAssets): string | undefined => {
  const normalizedExt = ext.toLowerCase();

  if (normalizedExt === 'svg' || normalizedExt === 'psd' || normalizedExt === 'eps') {
    return toExportableImgixUrl(assets.image) ?? toExportableImgixUrl(assets.original);
  }

  return toExportableImgixUrl(assets.original) ?? toExportableImgixUrl(assets.image);
};

export const isClipExportable = (clip: { ext: string; assets: ImgixExportSourceAssets }): boolean =>
  extCanBeExported(clip.ext) && !!getBestImgixExportSource(clip.ext, clip.assets);

/** This type represents the extensions/formats which can be exported from Imgix */
export type ImgixSupportedExportFormat =
  | 'jpg'
  | 'png'
  // | 'jp2'
  // | 'json'
  // | 'mp4' we aren't allowing video
  // | 'png8'
  // | 'png32'
  // | 'webm'
  | 'webp';
// | 'blurhash';

/** These are the list of formats we allow our users to export files as */
export const SUPPORTED_EXPORT_FORMATS = [
  'jpg',
  'png',
  // 'jp2',
  // 'json',
  // 'jxr',
  // 'pjpg',
  // 'mp4', we aren't allowing video
  // 'png8',
  // 'png32',
  // 'webm',
  'webp',
  // 'blurhash',
];

/** This function indicates whether or not the extension passed can be used as an export format */
export const extIsSupportedExportFormat = (ext: string): ext is ImgixSupportedExportFormat =>
  /**
   * @description List of supported Imgix export formats
   * @see https://docs.imgix.com/apis/rendering/format/fm
   */
  SUPPORTED_EXPORT_FORMATS.includes(ext.toLowerCase());

export type ImgixSupportedAsAssetTransformExportFormat = 'jpg' | 'jpeg' | 'png' | 'webp';

export const isExtSupportedAsAssetTransformExportFormat = (
  ext: string,
): ext is ImgixSupportedAsAssetTransformExportFormat => ['jpg', 'jpeg', 'png', 'webp'].includes(ext.toLowerCase());

/**
 * @description List of support image resize options
 * @see https://docs.imgix.com/apis/rendering/size/fit
 */
export type ImgixSupportedResizeFitMode =
  | 'clamp'
  | 'clip'
  | 'crop'
  | 'facearea'
  | 'fill'
  | 'fillmax'
  | 'max'
  | 'min'
  | 'scale';

/**
 * Crop anchor for imgix's `crop` param. Only the corner (top-left) anchor is expressed
 * explicitly — imgix center-crops by default when `fit=crop`, so center mode omits `crop`
 * entirely rather than passing an invalid `crop=center`.
 */
export type ImgixCropAnchor = 'top,left';

type CreateImgixImageUrlParams = {
  imageSrc: string;
  width: number;
  height: number;
  format: ImgixSupportedExportFormat;
  fileName?: string;
  fit?: ImgixSupportedResizeFitMode;
  auto?: 'auto';
  crop?: ImgixCropAnchor;
};

/**
 *
 * @description By passing in an image's height, width, extension (i.e. format), along with its source, we use imgix's buildUrl function to create an image url. If you'd like to use a different fit than the default, you can also pass one in
 * @returns {string} an image url for the newly formatted image
 * @see https://docs.imgix.com/libraries/react-imgix#general-advanced-usage
 */
export const createImgixImageUrl = ({
  auto,
  crop,
  fileName,
  fit = 'crop',
  format,
  height,
  imageSrc,
  width,
}: CreateImgixImageUrlParams) => {
  const baseParams = { w: width, h: height, fm: format, fit };
  const options = { disableLibraryParam: true, src: imageSrc };

  const paramsWithCrop = crop ? { ...baseParams, crop } : baseParams;
  const paramsWithAuto = auto ? { ...paramsWithCrop, auto } : paramsWithCrop;
  const paramsWithFileName = fileName ? { ...paramsWithAuto, dl: fileName } : paramsWithAuto;

  return buildClientImgixUrl(imageSrc, paramsWithFileName, options);
};
